import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { profileApi } from '../../services/backendApi';
import { Alert, Card, CardBody, Spinner } from '../../components';
import { DirectoryFilterBar } from '../../components/DirectoryFilterBar';
import { WorkerTable } from '../manager/SearchWorkers';
import { siteDisplayName, tenantDisplayName } from '../../utils';
import { useTenants } from '../../contexts/TenantContext';
import type { Profile } from '../../types';

export const VerifierWorkers: React.FC = () => {
  const { tenants } = useTenants();
  const [params, setParams] = useSearchParams();
  const tenantId = params.get('tenant') ?? '';
  const siteId = params.get('site') ?? '';
  const search = params.get('q') ?? '';
  const fieldFilter = params.get('field') ?? '';
  const groupBySite = params.get('group') === '1';

  const [employees, setEmployees] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === 'tenant') next.delete('site');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setLoading(true);
      profileApi
        .getWorkers({
          name: search || undefined,
          fieldOfWork: fieldFilter || undefined,
          tenantId: tenantId || undefined,
          siteId: siteId || undefined,
          limit: 100,
        })
        .then(result => setEmployees(result ?? []))
        .catch(() => setError('Failed to load workers'))
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [tenantId, siteId, search, fieldFilter]);

  const grouped = useMemo(() => {
    if (!groupBySite) return null;
    const map = new Map<string, Profile[]>();
    for (const worker of employees) {
      const key = `${worker.tenant_id ?? 'none'}:${worker.tenant_site_id ?? 'none'}`;
      const list = map.get(key) ?? [];
      list.push(worker);
      map.set(key, list);
    }
    return [...map.entries()].map(([, list]) => [list[0]?.tenant_site_id || 'none', list] as [string, Profile[]]);
  }, [employees, groupBySite]);

  const resultLabel = `${employees.length} worker${employees.length === 1 ? '' : 's'}${
    tenantId ? ` · ${tenantDisplayName(tenants, tenantId)}` : ''
  }${siteId ? ` · ${siteDisplayName(tenants, tenantId, siteId)}` : ''}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Workers</h1>
        <p className="text-sm text-gray-500 mt-1">Workers in the tenants and sites you can access.</p>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>}

      <DirectoryFilterBar
        search={search}
        onSearch={value => setFilter('q', value)}
        fieldFilter={fieldFilter}
        onFieldFilter={value => setFilter('field', value)}
        tenantId={tenantId}
        onTenantId={value => setFilter('tenant', value)}
        siteId={siteId}
        onSiteId={value => setFilter('site', value)}
        showTenant
        groupBySite={groupBySite}
        onGroupBySite={value => setFilter('group', value ? '1' : '')}
        resultLabel={resultLabel}
        onClear={() => setParams({}, { replace: true })}
        hasActiveFilters={Boolean(tenantId || siteId || search || fieldFilter)}
      />

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : employees.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-center text-gray-500 py-12">
              {tenantId || siteId || search || fieldFilter
                ? 'No workers match the current filters.'
                : 'No employees assigned yet.'}
            </p>
          </CardBody>
        </Card>
      ) : (
        <WorkerTable
          employees={employees}
          grouped={grouped}
          showTenant
          linkPrefix="/verifier/workers"
          actionLabel="Open"
        />
      )}
    </div>
  );
};
